// src/pages/TaskForm.jsx
// V15 ENTERPRISE — Airbnb / Zillow / QuintoAndar level
// =====================================================
// 🔥 V15: DataContext como fuente única de verdad
// 🔥 V15: Single Source — SOLO DataContext para carga
// 🔥 V15: Single Writer — SOLO DataContext para crear/actualizar
// 🔥 V15: SyncManager manejado INTERNAMENTE por la estrategia
// 🔥 V15.1: Fix — await DataContext.getInstance + Promise.race 8s timeout
// =====================================================
import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Save,
  ArrowLeft,
  AlertCircle,
  ClipboardList,
  Calendar,
  User,
  Home,
  Flag,
  CheckCircle2,
  Clock,
  AlertTriangle,
} from "lucide-react";
import { useAuth } from "../contexts/useAuth";
import { useNotifications } from "../contexts/NotificationsContext";
import { useIsMobile } from "../hooks/useMediaQuery";

// 🔥 V15: Importar DataContext como fuente única de verdad
import DataContext from "../lib/data/context/DataContext";

/**
 * 🚀 TASK FORM - Formulário de Tarefas Ultra Profissional V15.1
 * 
 * Características:
 * ✅ DataContext como fuente única de verdad (SINGLE SOURCE)
 * ✅ Single Writer — solo DataContext escribe
 * ✅ SyncManager manejado INTERNAMENTE
 * ✅ Design responsivo y moderno
 * ✅ White Label completo (cores personalizadas)
 * ✅ Validações robustas
 * ✅ Atalhos de teclado (Ctrl+Enter)
 * ✅ Preview em tempo real
 * ✅ Confirmação ao sair com dados não salvos
 * ✅ Acessibilidade (ARIA labels)
 * ✅ Feedback visual em todas as ações
 * ✅ Notificações globais com Toast
 * ✅ Adaptado para dispositivos móveis
 * ✅ Timeout 8s para evitar UI congelada
 */

export default function TaskForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { database, user } = useAuth();
  const { showToast } = useNotifications();
  const isMobile = useIsMobile();
  
  const titleInputRef = useRef(null);
  const formRef = useRef(null);

  const isEdit = Boolean(id);

  // 🔥 V15: Obtener userId para DataContext
  const authUserId = useMemo(() => {
    try {
      const stored = localStorage.getItem('auth_user');
      return stored ? JSON.parse(stored).id : user?.uid || null;
    } catch { return user?.uid || null; }
  }, [user?.uid]);

  // 🔥 V15.1: Instancia de DataContext como fuente única de verdad (CORREGIDO)
  const ctxRef = useRef(null);
  const [ctxReady, setCtxReady] = useState(false);

  // Inicializar DataContext con await
  useEffect(() => {
    let cancelled = false;

    const initContext = async () => {
      if (!authUserId) return;
      
      try {
        // 🔥 CRÍTICO: await para resolver la Promise
        const ctx = await DataContext.getInstance(authUserId);
        if (!cancelled && ctx) {
          ctxRef.current = ctx;
          setCtxReady(true);
        }
      } catch (error) {
        console.error('❌ [TaskForm] Error inicializando DataContext:', error);
        if (!cancelled) {
          showToast("Erro ao conectar com o banco de dados", "error", { title: "Erro" });
        }
      }
    };

    initContext();
    return () => { cancelled = true; };
  }, [authUserId, showToast]);

  // Helper para obtener contexto de forma segura
  const getContext = useCallback(() => {
    return ctxRef.current || null;
  }, []);

  // ================================
  // 🎯 ESTADOS
  // ================================
  const [clients, setClients] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showExitDialog, setShowExitDialog] = useState(false);

  const [form, setForm] = useState({
    title: "",
    description: "",
    status: "pending",
    priority: "medium",
    dueDate: "",
    clientId: "",
    propertyId: "",
  });

  // ================================
  // 🎨 WHITE LABEL - Cores personalizadas
  // ================================
  const primaryColor = useMemo(() => {
    return getComputedStyle(document.documentElement)
      .getPropertyValue('--primary-color').trim() || '#2563eb';
  }, []);

  // ================================
  // 📅 DATA MÍNIMA (hoje)
  // ================================
  const today = new Date().toISOString().split('T')[0];

  // ================================
  // 🔥 V15.1: CARREGAR DADOS — SINGLE SOURCE (SOLO DataContext)
  // ================================
  useEffect(() => {
    let cancelled = false;

    const loadData = async () => {
      if (!ctxReady) return;

      setLoading(true);
      try {
        const ctx = getContext();

        if (!ctx) {
          if (!cancelled) {
            setError("Sistema não inicializado. Tente novamente.");
            setLoading(false);
          }
          return;
        }

        // 🔥 V15: Cargar clientes desde DataContext (SIN fallback)
        const clientsData = await ctx.collections.clients.read();
        if (!cancelled) setClients(Array.isArray(clientsData) ? clientsData : []);

        // 🔥 V15: Cargar propiedades desde DataContext (SIN fallback)
        const propertiesData = await ctx.collections.properties.read();
        if (!cancelled) setProperties(Array.isArray(propertiesData) ? propertiesData : []);

        // 🔥 V15: Cargar tarea existente desde DataContext (SIN fallback)
        if (isEdit && id) {
          const task = await ctx.collections.tasks.findById(id);

          if (cancelled) return;

          if (task) {
            setForm({
              title: task.title || "",
              description: task.description || "",
              status: task.status || "pending",
              priority: task.priority || "medium",
              dueDate: task.dueDate ? task.dueDate.split('T')[0] : "",
              clientId: task.clientId || "",
              propertyId: task.propertyId || "",
            });
          } else {
            setError("Tarefa não encontrada");
            showToast("Tarefa não encontrada", "error", { title: "Erro" });
            setTimeout(() => navigate("/tarefas"), 2000);
          }
        }

        if (!cancelled) {
          setTimeout(() => {
            titleInputRef.current?.focus();
          }, 100);
        }

      } catch (error) {
        if (!cancelled) {
          console.error('❌ [V15 TaskForm] Erro ao carregar dados:', error);
          setError("Erro ao carregar dados. Tente novamente.");
          showToast("Não foi possível carregar os dados", "error", { title: "Erro" });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadData();

    return () => { cancelled = true; };
  }, [id, isEdit, ctxReady, getContext, navigate, showToast]);

  // ================================
  // 🚨 PROMPT AO SAIR COM DADOS NÃO SALVOS
  // ================================
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // ================================
  // ⌨️ ATALHO CTRL+ENTER (solo desktop)
  // ================================
  useEffect(() => {
    if (isMobile) return;
    
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key === 'Enter' && !saving) {
        e.preventDefault();
        formRef.current?.requestSubmit();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [saving, isMobile]);

  // ================================
  // 📊 CLIENTES E IMÓVEIS MEMOIZADOS
  // ================================
  const activeClients = useMemo(() => {
    return clients.filter(c => c.status !== 'inactive' && c.status !== 'Inativo');
  }, [clients]);

  const availableProperties = useMemo(() => {
    return properties.filter(p => p.status === 'Disponível' || p.status === 'available');
  }, [properties]);

  // ================================
  // 🎨 HELPERS VISUAIS
  // ================================
  const getStatusBadge = useCallback((status) => {
    const badges = {
      pending: { label: 'Pendente', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      in_progress: { label: 'Em progresso', color: 'bg-blue-100 text-blue-800', icon: Flag },
      done: { label: 'Concluída', color: 'bg-emerald-100 text-emerald-800', icon: CheckCircle2 }
    };
    return badges[status] || badges.pending;
  }, []);

  const getPriorityBadge = useCallback((priority) => {
    const badges = {
      low: { label: 'Baixa', color: 'bg-muted text-slate-800' },
      medium: { label: 'Média', color: 'bg-orange-100 text-orange-800' },
      high: { label: 'Alta', color: 'bg-rose-100 text-rose-800' }
    };
    return badges[priority] || badges.medium;
  }, []);

  // ================================
  // 🔄 HANDLERS COM useCallback
  // ================================
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setForm((prev) => {
      const newForm = { ...prev, [name]: value };
      setHasUnsavedChanges(true);
      return newForm;
    });
  }, []);

  // ================================
  // 🔥 V15.1: HANDLE SUBMIT — SINGLE WRITER (SOLO DataContext)
  // 🔥 CORREGIDO: try/catch/finally + timeout 8s + validación ctx
  // ================================
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setError("");

    // Validación de título
    if (!form.title?.trim()) {
      setError("O título é obrigatório.");
      titleInputRef.current?.focus();
      return;
    }

    if (form.title.trim().length < 3) {
      setError("O título deve ter pelo menos 3 caracteres.");
      titleInputRef.current?.focus();
      return;
    }

    // 🔥 CRÍTICO: Validar que el contexto existe antes de intentar guardar
    const ctx = getContext();
    
    if (!ctx) {
      setError("Sistema não disponível. Tente novamente.");
      showToast("Erro de conexão com o banco de dados", "error", { title: "Erro" });
      return;
    }

    if (!ctx.collections?.tasks) {
      setError("Serviço de tarefas não disponível. Tente novamente.");
      showToast("Serviço de tarefas indisponível", "error", { title: "Erro" });
      return;
    }

    // 🔥 CRÍTICO: setSaving(true) al inicio
    setSaving(true);

    try {
      const taskData = {
        ...form,
        title: form.title.trim(),
        description: form.description?.trim() || "",
        dueDate: form.dueDate || null,
        clientId: form.clientId || null,
        propertyId: form.propertyId || null,
        updatedAt: new Date().toISOString(),
      };

      // 🔥 V15.1: Timeout de 8 segundos con Promise.race
      const TIMEOUT_MS = 8000;

      if (isEdit && id) {
        // 🔥 MODO EDICIÓN: update con timeout
        const updatePromise = ctx.collections.tasks.update(id, taskData);
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error("Tempo limite excedido ao atualizar")), TIMEOUT_MS);
        });

        await Promise.race([updatePromise, timeoutPromise]);

        showToast("As alterações foram salvas com sucesso.", "success", {
          title: "✅ Tarefa atualizada"
        });
      } else {
        // 🔥 MODO NUEVO: create con timeout
        const newTaskData = {
          ...taskData,
          createdAt: new Date().toISOString(),
          userId: authUserId,
        };

        const createPromise = ctx.collections.tasks.create(newTaskData);
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error("Tempo limite excedido ao criar")), TIMEOUT_MS);
        });

        await Promise.race([createPromise, timeoutPromise]);

        showToast("A nova tarefa foi criada com sucesso.", "success", {
          title: "🎉 Tarefa criada"
        });
      }

      // Solo navegar si el guardado fue exitoso
      setHasUnsavedChanges(false);
      navigate("/tarefas");
      
    } catch (error) {
      console.error('❌ [V15 TaskForm] Erro ao salvar:', error);
      
      const errorMessage = error?.message || "Ocorreu um erro ao salvar a tarefa.";
      setError(errorMessage);
      showToast(errorMessage, "error", { title: "Erro ao salvar" });
      
      // 🔥 CRÍTICO: NO navegar si hubo error — el usuario se queda en el formulario
    } finally {
      // 🔥 CRÍTICO: SIEMPRE liberar loading, incluso si hay error
      setSaving(false);
    }
  }, [form, isEdit, id, getContext, showToast, navigate, authUserId]);

  const handleCancel = useCallback(() => {
    if (hasUnsavedChanges) {
      setShowExitDialog(true);
    } else {
      navigate("/tarefas");
    }
  }, [hasUnsavedChanges, navigate]);

  // ================================
  // 🎨 LOADING SKELETON - ADAPTADO
  // ================================
  if (loading) {
    return (
      <div className={isMobile ? "p-3 max-w-3xl mx-auto space-y-4" : "p-4 md:p-6 max-w-3xl mx-auto space-y-6"}>
        <div className={`${isMobile ? 'h-10' : 'h-12'} bg-muted rounded-xl animate-pulse`}></div>
        <div className={`bg-white rounded-2xl border border-color space-y-4 ${isMobile ? 'p-4' : 'p-6'}`}>
          {[1,2,3,4,5,6].map(i => (
            <div key={i} className={`${isMobile ? 'h-12' : 'h-16'} bg-muted rounded-xl animate-pulse`}></div>
          ))}
        </div>
      </div>
    );
  }

  const StatusPreview = getStatusBadge(form.status);
  const StatusIcon = StatusPreview.icon;
  const PriorityPreview = getPriorityBadge(form.priority);

  return (
    <>
      <div className={isMobile ? "p-3 max-w-3xl mx-auto space-y-4" : "p-4 md:p-6 max-w-3xl mx-auto space-y-6"}>
        
        {/* HEADER */}
        <div className={`flex ${isMobile ? 'flex-col' : 'flex-col sm:flex-row sm:items-center'} justify-between gap-3`}>
          <div className="flex items-center gap-2">
            <div 
              className={`rounded-xl ${isMobile ? 'p-1.5' : 'p-2'}`}
              style={{ backgroundColor: `${primaryColor}20` }}
            >
              <ClipboardList
                className={isMobile ? 'w-4 h-4' : 'w-5 h-5 md:w-6 md:h-6'}
                style={{ color: primaryColor }}
              />
            </div>
            <div>
              <h1 className={`font-bold text-primary ${isMobile ? 'text-lg' : 'text-xl md:text-2xl'}`}>
                {isEdit ? (isMobile ? "Editar" : "Editar Tarefa") : (isMobile ? "Nova" : "Nova Tarefa")}
              </h1>
              {!isMobile && (
                <p className="text-sm text-muted">
                  {isEdit ? "Altere os dados da tarefa" : "Preencha os dados da nova tarefa"}
                </p>
              )}
            </div>
          </div>

          <button
            onClick={handleCancel}
            className={`flex items-center gap-2 text-secondary hover:text-primary transition-colors self-start ${
              isMobile ? 'text-sm' : ''
            }`}
            aria-label="Voltar"
          >
            <ArrowLeft size={isMobile ? 16 : 18} />
            <span>Voltar</span>
          </button>
        </div>

        {/* FORM */}
        <form
          ref={formRef}
          onSubmit={handleSubmit}
          className={`bg-white rounded-xl md:rounded-2xl shadow-sm border border-color ${
            isMobile ? 'p-4 space-y-4' : 'p-4 md:p-6 space-y-6'
          }`}
        >
          
          {/* PREVIEW RÁPIDO */}
          {!isMobile && (
            <div className="flex flex-wrap items-center gap-3 p-4 bg-muted rounded-xl">
              <span className="text-sm font-medium text-secondary">Preview:</span>
              <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${StatusPreview.color}`}>
                <StatusIcon size={12} />
                {StatusPreview.label}
              </span>
              <span className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${PriorityPreview.color}`}>
                {PriorityPreview.label}
              </span>
              {form.dueDate && (
                <span className="inline-flex items-center gap-1 text-xs text-secondary">
                  <Calendar size={12} />
                  {new Date(form.dueDate).toLocaleDateString('pt-BR')}
                </span>
              )}
            </div>
          )}

          {/* ERROR */}
          {error && (
            <div className={`flex items-center gap-2 bg-rose-50 border border-rose-200 text-rose-700 rounded-xl ${
              isMobile ? 'p-3 text-xs' : 'px-4 py-3 text-sm'
            }`}>
              <AlertCircle className={`${isMobile ? 'w-3 h-3' : 'w-4 h-4'} flex-shrink-0`} />
              <span>{error}</span>
            </div>
          )}

          {/* TÍTULO */}
          <div>
            <label className={`block font-semibold text-secondary mb-1 ${isMobile ? 'text-xs' : 'text-sm'}`}>
              Título <span className="text-rose-500">*</span>
            </label>
            <input
              ref={titleInputRef}
              type="text"
              name="title"
              value={form.title}
              onChange={handleChange}
              className={`w-full rounded-xl border border-color focus:ring-2 outline-none transition-shadow ${
                isMobile ? 'px-3 py-2.5 text-sm' : 'px-4 py-3'
              }`}
              style={{ 
                borderColor: error && !form.title ? '#fecaca' : undefined
              }}
              placeholder={isMobile ? "Título da tarefa" : "Ex: Ligar para cliente sobre financiamento"}
              maxLength={100}
              aria-label="Título da tarefa"
              required
            />
            <div className="flex justify-end mt-1">
              <span className={`text-muted ${isMobile ? 'text-[10px]' : 'text-xs'}`}>
                {form.title.length}/100
              </span>
            </div>
          </div>

          {/* DESCRIÇÃO */}
          <div>
            <label className={`block font-semibold text-secondary mb-1 ${isMobile ? 'text-xs' : 'text-sm'}`}>
              Descrição
            </label>
            <textarea
              name="description"
              value={form.description}
              onChange={handleChange}
              rows={isMobile ? 3 : 4}
              className={`w-full rounded-xl border border-color focus:ring-2 focus:ring-[var(--primary-color)]/40 outline-none resize-none ${
                isMobile ? 'px-3 py-2 text-sm' : 'px-4 py-3'
              }`}
              placeholder={isMobile ? "Detalhes da tarefa..." : "Detalhes da tarefa, observações, etc..."}
              maxLength={500}
              aria-label="Descrição da tarefa"
            />
            <div className="flex justify-end mt-1">
              <span className={`text-muted ${isMobile ? 'text-[10px]' : 'text-xs'}`}>
                {form.description.length}/500
              </span>
            </div>
          </div>

          {/* STATUS + PRIORIDADE */}
          <div className={`grid ${isMobile ? 'grid-cols-1 gap-3' : 'grid-cols-1 sm:grid-cols-2 gap-4'}`}>
            <div>
              <label className={`block font-semibold text-secondary mb-1 ${isMobile ? 'text-xs' : 'text-sm'}`}>
                Estado
              </label>
              <select
                name="status"
                value={form.status}
                onChange={handleChange}
                className={`w-full rounded-xl border border-color focus:ring-2 outline-none ${
                  isMobile ? 'px-3 py-2.5 text-sm' : 'px-4 py-3'
                }`}
                aria-label="Status da tarefa"
              >
                <option value="pending">⏳ Pendente</option>
                <option value="in_progress">🚀 Em progresso</option>
                <option value="done">✅ Concluída</option>
              </select>
            </div>

            <div>
              <label className={`block font-semibold text-secondary mb-1 ${isMobile ? 'text-xs' : 'text-sm'}`}>
                Prioridade
              </label>
              <select
                name="priority"
                value={form.priority}
                onChange={handleChange}
                className={`w-full rounded-xl border border-color focus:ring-2 outline-none ${
                  isMobile ? 'px-3 py-2.5 text-sm' : 'px-4 py-3'
                }`}
                aria-label="Prioridade da tarefa"
              >
                <option value="low">🔵 Baixa</option>
                <option value="medium">🟠 Média</option>
                <option value="high">🔴 Alta</option>
              </select>
            </div>
          </div>

          {/* DATA */}
          <div>
            <label className={`block font-semibold text-secondary mb-1 ${isMobile ? 'text-xs' : 'text-sm'}`}>
              Data limite
            </label>
            <input
              type="date"
              name="dueDate"
              value={form.dueDate}
              onChange={handleChange}
              min={today}
              className={`w-full rounded-xl border border-color focus:ring-2 outline-none ${
                isMobile ? 'px-3 py-2.5 text-sm' : 'px-4 py-3'
              }`}
              aria-label="Data limite da tarefa"
            />
            {form.dueDate && form.dueDate < today && (
              <p className={`flex items-center gap-1 text-amber-600 mt-1 ${
                isMobile ? 'text-[10px]' : 'text-xs'
              }`}>
                <AlertTriangle size={isMobile ? 10 : 12} />
                Data no passado. Tarefa será marcada como atrasada.
              </p>
            )}
          </div>

          {/* CLIENTE */}
          <div>
            <label className={`block font-semibold text-secondary mb-1 ${isMobile ? 'text-xs' : 'text-sm'}`}>
              <div className="flex items-center gap-1">
                <User size={isMobile ? 12 : 14} />
                Cliente
              </div>
            </label>
            <select
              name="clientId"
              value={form.clientId}
              onChange={handleChange}
              className={`w-full rounded-xl border border-color focus:ring-2 outline-none ${
                isMobile ? 'px-3 py-2.5 text-sm' : 'px-4 py-3'
              }`}
              aria-label="Selecionar cliente"
            >
              <option value="">— Nenhum cliente —</option>
              {activeClients.length > 0 ? (
                activeClients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.name} {!isMobile && client.email ? `(${client.email})` : ''}
                  </option>
                ))
              ) : (
                <option value="" disabled>Nenhum cliente ativo</option>
              )}
            </select>
          </div>

          {/* IMÓVEL */}
          <div>
            <label className={`block font-semibold text-secondary mb-1 ${isMobile ? 'text-xs' : 'text-sm'}`}>
              <div className="flex items-center gap-1">
                <Home size={isMobile ? 12 : 14} />
                Imóvel
              </div>
            </label>
            <select
              name="propertyId"
              value={form.propertyId}
              onChange={handleChange}
              className={`w-full rounded-xl border border-color focus:ring-2 outline-none ${
                isMobile ? 'px-3 py-2.5 text-sm' : 'px-4 py-3'
              }`}
              aria-label="Selecionar imóvel"
            >
              <option value="">— Nenhum imóvel —</option>
              {availableProperties.length > 0 ? (
                availableProperties.map((property) => (
                  <option key={property.id} value={property.id}>
                    {property.title} {!isMobile && property.city ? `- ${property.city}` : ''}
                  </option>
                ))
              ) : (
                <option value="" disabled>Nenhum imóvel disponível</option>
              )}
            </select>
          </div>

          {/* AÇÕES */}
          <div className={`flex ${isMobile ? 'flex-col gap-2' : 'flex-col-reverse sm:flex-row justify-end gap-3'} pt-4 border-t border-color`}>
            <button
              type="button"
              onClick={handleCancel}
              className={`rounded-xl border border-color hover:bg-muted transition font-medium ${
                isMobile 
                  ? 'px-4 py-3 text-sm w-full' 
                  : 'px-5 py-3 text-sm md:text-base'
              }`}
              disabled={saving}
            >
              Cancelar
            </button>

            <button
              type="submit"
              disabled={saving}
              className={`flex items-center justify-center gap-2 rounded-xl text-white font-semibold shadow-sm transition-all disabled:opacity-60 disabled:cursor-not-allowed hover:opacity-90 active:scale-[0.98] ${
                isMobile 
                  ? 'px-4 py-3 text-sm w-full' 
                  : 'px-5 py-3 text-sm md:text-base'
              }`}
              style={{ backgroundColor: primaryColor }}
              aria-label={isEdit ? "Atualizar tarefa" : "Salvar tarefa"}
            >
              {saving ? (
                <>
                  <div className={`${isMobile ? 'w-3 h-3' : 'w-4 h-4'} border-2 border-white border-t-transparent rounded-full animate-spin`}></div>
                  {isEdit ? (isMobile ? "Atualizando" : "Atualizando...") : (isMobile ? "Salvando" : "Salvando...")}
                </>
              ) : (
                <>
                  <Save size={isMobile ? 16 : 18} />
                  {isEdit ? (isMobile ? "Atualizar" : "Atualizar") : (isMobile ? "Salvar" : "Salvar")}
                  {!isMobile && (
                    <span className="hidden sm:inline text-xs opacity-75 ml-1">
                      (Ctrl+Enter)
                    </span>
                  )}
                </>
              )}
            </button>
          </div>

          {/* DICA DE ATALHO MOBILE */}
          {isMobile && !saving && (
            <p className="text-[10px] text-center text-muted">
              Toque fora para fechar
            </p>
          )}
        </form>
      </div>

      {/* MODAL DE CONFIRMAÇÃO AO SAIR */}
      {showExitDialog && (
        <div 
          className="fixed inset-0 bg-black/40 backdrop-blur-[2px] flex items-center justify-center z-50 p-3"
          onClick={() => setShowExitDialog(false)}
        >
          <div 
            className={`bg-white rounded-xl md:rounded-2xl shadow-lg max-w-md w-full border border-color ${
              isMobile ? 'p-4 space-y-3' : 'p-5 md:p-6 space-y-4'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2">
              <div className={`${isMobile ? 'p-1.5' : 'p-2'} bg-amber-100 rounded-lg`}>
                <AlertTriangle className={isMobile ? 'w-4 h-4' : 'w-5 h-5'} style={{ color: '#f59e0b' }} />
              </div>
              <h2 className={`font-bold text-primary ${isMobile ? 'text-base' : 'text-lg'}`}>
                Descartar alterações?
              </h2>
            </div>

            <p className={`text-secondary ${isMobile ? 'text-sm' : ''}`}>
              Você tem alterações não salvas. Deseja realmente sair?
            </p>

            <div className={`flex ${isMobile ? 'flex-col gap-2' : 'flex-col-reverse sm:flex-row justify-end gap-3'} pt-2`}>
              <button
                onClick={() => setShowExitDialog(false)}
                className={`border border-color rounded-lg hover:bg-muted transition-colors ${
                  isMobile ? 'px-4 py-3 text-sm w-full' : 'px-4 py-2'
                }`}
              >
                Continuar editando
              </button>

              <button
                onClick={() => {
                  setShowExitDialog(false);
                  setHasUnsavedChanges(false);
                  navigate("/tarefas");
                }}
                className={`bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors font-semibold ${
                  isMobile ? 'px-4 py-3 text-sm w-full' : 'px-4 py-2'
                }`}
              >
                Descartar e sair
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}